import React, { useEffect, useRef, useState } from 'react';
import Spacing from '../Spacing';
import { Drawer } from 'vaul';
import { checkEligibility } from '../../services/npiManagement';
import Skeleton from 'react-loading-skeleton';
import { Copy, X } from 'lucide-react';
import { notify } from '../../helpers';

const grid = (cols) => ({
  gridTemplateColumns: cols || "repeat(auto-fit, minmax(140px, 1fr))",
  alignItems: "start",
});

export default function EligibilitySingleViewModal(props) {
  const {
    patient_data = {},
    activeModal = false,
    setActiveModal = () => {},
    previousScrollPosition = 0,
    checkEligibilityFromWaiting = false,
  } = props;

  const [eligibilityData, setEligibilityData] = useState({});
  const [eligibilityDataLoader, setEligibilityDataLoader] = useState(true);
  const [eligibilityErrorLog, setEligibilityErrorLog] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (activeModal) { 
      loadEligibilityData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal]);

  const loadEligibilityData = async () => {
    const syncData = {
      account_id: patient_data?.account_id,
      location_id: patient_data?.location_id,
      patient_id: checkEligibilityFromWaiting ? patient_data?.patient_id : patient_data?.id,
      waiting_room_id: checkEligibilityFromWaiting ? patient_data?.id : null,
    };
    setEligibilityDataLoader(true);
    try {
      const response = await checkEligibility(syncData);
      if(response){
        if(response?.detail) {
          setEligibilityErrorLog(response);
        } else {
          if(response?.data?.status === "success"){
            setEligibilityData(response?.data?.result);
            setEligibilityErrorLog({});
          } else {
            setEligibilityErrorLog(response?.data);
          }
        }
      }
    } catch (error) {
      setEligibilityErrorLog(error);
    } finally {
      setEligibilityDataLoader(false);
    }
  };

  const formatDateAsMDY = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "-";

    // Extract only the first 8 digits (handles "20250813-99991231" case)
    const cleanDate = dateStr.replace(/[^0-9]/g, "").slice(0, 8);
    if (cleanDate?.length !== 8) return "-";

    const year = cleanDate.slice(0, 4);
    const month = cleanDate.slice(4, 6);
    const day = cleanDate.slice(6, 8);

    return `${month}/${day}/${year}`;
  };

  const handleCopy = async () => {
    try {
      const dataToCopy = eligibilityErrorLog?.raw_request || eligibilityErrorLog;
      const content = JSON.stringify(dataToCopy ?? {}, null, 2);
      await navigator.clipboard.writeText(content);
      notify("success", "Logs copied to clipboard!", 0.5);
    } catch (err) {
      console.error("Failed to copy logs:", err);
      notify("error", "Failed to copy logs!", 0.5);
    }
  };

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose} dismissible={false}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <Drawer.Title className="sr-only">View Eligibility</Drawer.Title>
            <Drawer.Description className="sr-only">View Individual Eligibility</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">
              <div className="docpro_modal">
                <button
                  className="docpro_close_btn"
                  onClick={() => handleDrawerClose()}
                >
                  <X />
                </button>

                {eligibilityDataLoader ? (
                  <>
                    <h2 className="docpro_fs_24 docpro_mb_0 text-center">Eligibility Details</h2>
                    <Spacing lg="10" sm="25" />

                    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8 }}>
                      <h3 className="docpro_section_title">Subscriber</h3>
                      <div className="docpro_section_body">
                        <div className="docpro_grid_flex_wrap">
                          <Skeleton width="6rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                        </div>
                        <Spacing lg="15" sm="20" />
                        <div className="docpro_grid_flex_wrap">
                          <Skeleton width="7rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="3rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="6rem" borderRadius="8px"></Skeleton>
                        </div>
                      </div>
                      <h3 className="docpro_section_title docpro_section_divider">Plan</h3>
                      <div className="docpro_section_body">
                        <div className="docpro_grid_flex_wrap">
                          <div>
                            <Skeleton width="7rem" borderRadius="8px"></Skeleton>
                            <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          </div>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <div>
                            <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                            <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          </div>
                          <Skeleton width="6rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="7rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                        </div>
                      </div>
                      <h3 className="docpro_section_title docpro_section_divider">Benefit - Related Entities</h3>
                      <div className="docpro_section_body">
                        <div style={{ display: "flex", flexWrap: "wrap" }}>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="7rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                        </div>
                      </div>

                      <h3 className="docpro_section_title docpro_section_divider">Primary Care Providers</h3>
                      <div className="docpro_section_body">
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("1fr 0.7fr 0.9fr 1.6fr 1.8fr"), paddingBottom: 5, borderBottom: "1px solid #f3f4f6" }}>
                          <div>Entity Name</div>
                          <div>Middle Name</div>
                          <div>Last Name</div>
                          <div>Date Information</div>
                          <div>Services</div>
                        </div>
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("1fr 0.7fr 0.9fr 1.6fr 1.8fr"), padding: "10px 0" }}>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="3rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <div style={{ display: "flex", flexWrap: "wrap" }}>
                            <Skeleton width="4.5rem" borderRadius="8px"></Skeleton>
                            <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          </div>
                        </div>
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("1fr 0.7fr 0.9fr 1.6fr 1.8fr"), padding: "10px 0", borderTop: "1px solid #f3f4f6" }}>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="3.5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4.5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <div style={{ display: "flex", flexWrap: "wrap" }}>
                            <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                            <Skeleton width="6rem" borderRadius="8px"></Skeleton>
                          </div>
                        </div>
                      </div>

                      <h3 className="docpro_section_title docpro_section_divider">
                        Financials&nbsp;&nbsp;
                        <span className="docpro_empty_badge">
                          <span className="docpro_fw_400" style={{ color: "#2563eb" }}>98</span>
                          <span className="docpro_fw_400" style={{ color: "#111827" }}>Professional (Physician) Visit - Office</span>
                        </span>
                      </h3>
                      <div className="docpro_section_body">
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("0.9fr 0.7fr 0.8fr 1.8fr 1fr"), paddingBottom: 5 }}>
                          <div>Entity Type</div>
                          <div>Amount</div>
                          <div>Coverage level</div>
                          <div>Message</div>
                          <div>Time period</div>
                        </div>
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("0.9fr 0.7fr 0.8fr 1.8fr 1fr"), padding: "10px 0" }}>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="3rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="7rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                        </div>
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("0.9fr 0.7fr 0.8fr 1.8fr 1fr"), padding: "10px 0", borderTop: "1px solid #f3f4f6" }}>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="3rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="7.5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="docpro_fs_24 docpro_mb_0 text-center">Eligibility Details</h2>
                    <Spacing lg="10" sm="25" />

                    {Object.keys(eligibilityErrorLog)?.length > 0 ?
                      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "20px 10px" }}>
                        <p className="docpro_section_label text-center docpro_red_color docpro_claim_error_message">{
                          eligibilityErrorLog?.detail 
                          ? eligibilityErrorLog?.detail 
                          : eligibilityErrorLog?.message 
                          ? eligibilityErrorLog?.message 
                          : eligibilityErrorLog?.response && eligibilityErrorLog?.response?.data && eligibilityErrorLog?.response?.data?.detail 
                          ? eligibilityErrorLog?.response?.data?.detail 
                          : ""
                        }</p>
                        <Spacing lg="15" sm="25" />
                        <div className="flex items-center" style={{ justifyContent: "center" }}>
                          <button
                            className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                            style={{ padding: "12px 25px" }}
                            onClick={() => { handleCopy(); }}
                          >
                            <Copy width={18} height={18} />
                            <span>Copy Logs</span>
                          </button>
                        </div>
                      </div>
                    :
                    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8 }}>
                      {/* ==================== Subscriber ==================== */}
                      <h3 className="docpro_section_title">Subscriber</h3>
                      <div className="docpro_section_body">
                        <div className="docpro_grid_flex_wrap">
                          <div>
                            <div className="docpro_section_label">First Name</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.firstName || "-"}</div>
                          </div>
                          <div>
                            <div className="docpro_section_label">Middle Name</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.middleName || "-"}</div>
                          </div>
                          <div>
                            <div className="docpro_section_label">Last Name</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.lastName || "-"}</div>
                          </div>
                          <div>
                            <div className="docpro_section_label">Date of Birth</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.dateOfBirth ? formatDateAsMDY(eligibilityData?.subscriber?.dateOfBirth) : "-"}</div>
                          </div>
                          <div>
                            <div className="docpro_section_label">Gender</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.gender ? eligibilityData?.subscriber?.gender === "M" ? "Male" : eligibilityData?.subscriber?.gender === "F" ? "Female" : "Others" : "-"}</div>
                          </div>
                          <div>
                            <div className="docpro_section_label">Member ID</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.memberId || "-"}</div>
                          </div>
                        </div>

                        <Spacing lg="15" sm="20" />
                        <div className="docpro_section_value" style={{ marginBottom: 8 }}>ADDRESS</div>
                        <div className="docpro_grid_flex_wrap">
                          <div>
                            <div className="docpro_section_label">Line 1</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.address?.address1 || "-"}</div>
                          </div>
                          <div>
                            <div className="docpro_section_label">Line 2</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.address?.address2 || "-"}</div>
                          </div>
                          <div>
                            <div className="docpro_section_label">City</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.address?.city || "-"}</div>
                          </div>
                          <div>
                            <div className="docpro_section_label">State</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.address?.state || "-"}</div>
                          </div>
                          <div>
                            <div className="docpro_section_label">Postal Code</div>
                            <div className="docpro_section_value">{eligibilityData?.subscriber?.address?.postalCode || "-"}</div>
                          </div>
                        </div>
                      </div>

                      {/* ==================== Plan ==================== */}
                      <h3 className="docpro_section_title docpro_section_divider">Plan</h3>
                      <div className="docpro_section_body">
                        <div className="docpro_grid_flex_wrap">
                          {(() => {
                            const raw = (eligibilityData?.benefitsInformation || [])
                              .filter(b => (b?.planCoverage && (b?.code === "1" || b?.name === "Active Coverage")))
                              .map(b => String(b.planCoverage).trim())
                              .filter(Boolean);

                            const seen = new Set();
                            const planNames = raw.filter(n => {
                              const key = n.toUpperCase();
                              if (seen.has(key)) return false;
                              seen.add(key);
                              return true;
                            });

                            if (planNames?.length === 0) return;
                            return (
                              <div>
                                <div className="docpro_section_label" style={{ marginBottom: 4 }}>Plan Names</div>
                                <div className="docpro_section_value">
                                  {planNames.length === 0 && <>-</>}
                                  {planNames.map((n, i) => (
                                    <div key={i} style={{ lineHeight: "21px" }}>{n}</div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                          {eligibilityData?.planInformation?.planNumber && (
                            <div>
                              <div className="docpro_section_label">Plan Number</div>
                              <div className="docpro_section_value">{eligibilityData?.planInformation?.planNumber || "-"}</div>
                            </div>
                          )}
                          {(() => {
                            const raw = (eligibilityData?.benefitsInformation || [])
                              .filter(b => b?.insuranceType && (b?.code === "1" || b?.name === "Active Coverage"))
                              .map(b => String(b.insuranceType).trim())
                              .filter(Boolean);

                            const seen = new Set();
                            const types = raw.filter(t => {
                              const k = t.toLowerCase();
                              if (seen.has(k)) return false;
                              seen.add(k);
                              return true;
                            });

                            if (types.length === 0) return ;
                            return (
                              <div>
                                <div className="docpro_section_label">Insurance Types</div>
                                <div className="docpro_section_value">
                                  {types.length === 0 && <>-</>}
                                  {types.map((t, i) => (
                                    <div key={i}>{t}</div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                          {eligibilityData?.subscriber?.groupNumber && (
                            <div>
                              <div className="docpro_section_label">Group Number</div>
                              <div className="docpro_section_value">{eligibilityData?.subscriber?.groupNumber || "-"}</div>
                            </div>
                          )}
                          {eligibilityData?.subscriber?.groupDescription && (
                            <div>
                              <div className="docpro_section_label">Group Description</div>
                              <div className="docpro_section_value">{eligibilityData?.subscriber?.groupDescription || "-"}</div>
                            </div>
                          )}
                          {eligibilityData?.planDateInformation?.plan && (
                            <div>
                              <div className="docpro_section_label">Plan Date</div>
                              <div className="docpro_section_value">{formatDateAsMDY(eligibilityData?.planDateInformation?.plan) || "-"}</div>
                            </div>
                          )}
                          {eligibilityData?.planDateInformation?.planBegin && (
                            <div>
                              <div className="docpro_section_label">Plan Begin</div>
                              <div className="docpro_section_value">{formatDateAsMDY(eligibilityData?.planDateInformation?.planBegin) || "-"}</div>
                            </div>
                          )}
                          {eligibilityData?.planDateInformation?.planEnd && (
                            <div>
                              <div className="docpro_section_label">Plan End</div>
                              <div className="docpro_section_value">{formatDateAsMDY(eligibilityData?.planDateInformation?.planEnd) || "-"}</div>
                            </div>
                          )}
                        </div>
                        {(() => {
                          const disclaimer = (eligibilityData?.benefitsInformation || [])
                            .find(b => String(b?.name).toLowerCase() === "benefit disclaimer" || b?.code === "P");

                          if (!disclaimer) return null;

                          const desc = disclaimer?.additionalInformation
                            ?.map(x => (x?.description || "").trim())
                            .filter(Boolean)
                            .join(" ") || null;

                          if (!desc) return null;

                          return (
                            <div>
                              <Spacing lg="15" sm="20" />
                              <span className="docpro_section_label docpro_fw_600">Benefit Disclaimer:&nbsp;</span>
                              <span className="docpro_section_value" style={{ textTransform: 'lowercase' }}>{desc}</span>
                            </div>
                          );
                        })()}
                      </div>

                      {/* ==================== Benefit - Related Entities ==================== */}
                      <h3 className="docpro_section_title docpro_section_divider">Benefit - Related Entities</h3>
                      <div className="docpro_section_body">
                        {/* header row */}
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("1.6fr 0.9fr 1fr 1.2fr"), paddingBottom: 5, borderBottom: "1px solid #f3f4f6" }}>
                          <div>Entity</div>
                          <div>Contact</div>
                          <div>Benefits Date</div>
                          <div>Associated benefits</div>
                        </div>

                        {/* rows */}
                        {(() => {
                          const bis = Array.isArray(eligibilityData?.benefitsInformation)
                            ? eligibilityData.benefitsInformation
                            : [];

                          const seen = new Set();
                          const rows = [];

                          // Build objects: {code, label}
                          const zipBenefits = (codes = [], names = []) =>
                            codes
                              .map((c, i) => ({
                                code: String(c ?? "").trim(),
                                label: String(names[i] ?? names[0] ?? "").trim(),
                              }))
                              .filter(({ code, label }) => code || label);

                          const fmtMDY = (yyyyMMdd) => {
                            if (!yyyyMMdd || typeof yyyyMMdd !== "string") return "-";
                            const onlyDigits = yyyyMMdd.replace(/[^0-9]/g, "");
                            if (onlyDigits.length === 16) {
                              const a = onlyDigits.slice(0, 8);
                              const b = onlyDigits.slice(8, 16);
                              const f = (d) => `${d.slice(4, 6)}/${d.slice(6, 8)}/${d.slice(0, 4)}`;
                              return `${f(a)} – ${f(b)}`;
                            }
                            if (onlyDigits.length === 8) {
                              const y = onlyDigits.slice(0, 4);
                              const m = onlyDigits.slice(4, 6);
                              const d = onlyDigits.slice(6, 8);
                              return `${m}/${d}/${y}`;
                            }
                            return "-";
                          };

                          const fmtBenefitDateInfo = (di = {}) => {
                            const key = Object.keys(di || {})[0];
                            if (!key) return "-";
                            const val = di[key];
                            return (
                              <>
                                <span style={{ color: "#6b7280", marginRight: 6 }}>{key}</span>
                                <span>{fmtMDY(String(val))}</span>
                              </>
                            );
                          };

                          const getContact = (ci) => {
                            const name = ci?.name?.trim();
                            const phone =
                              Array.isArray(ci?.contacts) && ci.contacts.length
                                ? ci.contacts.find((c) => c?.communicationMode === "Telephone")?.communicationNumber
                                : null;
                            return name || phone ? (
                              <div className="docpro_section_value">
                                {name ? <div>{name}</div> : null}
                                {phone ? (
                                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                    <span>{String(phone).replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")}</span>
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <div className="docpro_section_value">-</div>
                            );
                          };

                          const pushRow = ({ idx, keySuf, entity, di, services, benefitName }) => {
                            const name = (entity?.entityName || "").trim();

                            const sig = [
                              name.toLowerCase(),
                              (entity?.entityIdentifier || "").toLowerCase(),
                              (entity?.entityType || "").toLowerCase(),
                              String(benefitName || "").toLowerCase(),
                              JSON.stringify(di || {}),
                              services.map((s) => `${s.code}|${s.label}`.toLowerCase()).join("||"),
                            ].join("§");
                            if (seen.has(sig)) return;
                            seen.add(sig);

                            rows.push(
                              <div
                                key={`${idx}-${keySuf}`}
                                className="docpro_grid docpro_grid_gap_10"
                                style={{
                                  ...grid("1.6fr 0.9fr 1fr 1.2fr"),
                                  padding: "6px 0",
                                  borderTop: rows.length ? "1px solid #f3f4f6" : "none",
                                }}
                              >
                                <div className="docpro_section_value">
                                  <div style={{ fontWeight: 600 }}>{name || "-"}</div>
                                  <div style={{ color: "#6b7280", fontSize: 12 }}>
                                    {entity?.entityIdentifier || entity?.entityType
                                      ? `${entity?.entityIdentifier ?? ""}${entity?.entityType ? `\n${entity.entityType}` : ""}`
                                      : "-"}
                                  </div>
                                  {benefitName ? (
                                    <div style={{ color: "#6b7280", fontSize: 12 }}>{benefitName}</div>
                                  ) : null}
                                </div>

                                <div className="docpro_section_value">
                                  {getContact(entity?.contactInformation)}
                                </div>

                                <div className="docpro_section_value">
                                  {di ? fmtBenefitDateInfo(di) : "-"}
                                </div>

                                <div
                                  className="docpro_section_value"
                                  style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                                >
                                  {services.length ? (
                                    services.map((s, i) => (
                                      <span key={i} className="docpro_empty_badge" style={{ whiteSpace: 'pre-wrap' }}>
                                        <span style={{ color: "#2563eb", whiteSpace: 'nowrap' }}>{s.code}</span>
                                        <span>{s.label}</span>
                                      </span>
                                    ))
                                  ) : (
                                    <span style={{ color: "#6b7280" }}>-</span>
                                  )}
                                </div>
                              </div>
                            );
                          };

                          bis.forEach((bi, idx) => {
                            const services = zipBenefits(bi?.serviceTypeCodes, bi?.serviceTypes);
                            const di = bi?.benefitsDateInformation || null;
                            const benefitName = (bi?.name || "").trim();

                            if (bi?.benefitsRelatedEntity) {
                              pushRow({ idx, keySuf: "main", entity: bi.benefitsRelatedEntity, di, services, benefitName });
                            }
                            (bi?.benefitsRelatedEntities || []).forEach((e, j) =>
                              pushRow({ idx, keySuf: `rel-${j}`, entity: e, di, services, benefitName })
                            );
                          });

                          return rows.length ? (
                            rows
                          ) : (
                            <div style={{ padding: "10px 0", color: "#6b7280" }}>Entities not found</div>
                          );
                        })()}
                      </div>

                      <h3 className="docpro_section_title docpro_section_divider">Primary Care Providers</h3>
                      <div className="docpro_section_body">
                        {/* header row */}
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("1fr 0.7fr 0.9fr 1.6fr 1.8fr"), paddingBottom: 5, borderBottom: "1px solid #f3f4f6" }}>
                          <div>Entity Name</div>
                          <div>Middle Name</div>
                          <div>Last Name</div>
                          <div>Date Information</div>
                          <div>Services</div>
                        </div>

                        {/* rows */}
                        {(() => {
                          const pcps = (eligibilityData?.benefitsInformation || []).filter(
                            it => it?.code === "L" || String(it?.name).toLowerCase() === "primary care provider"
                          );

                          const seen = new Set();
                          const rows = [];

                          pcps.forEach((it, idx) => {
                            const di = it?.benefitsDateInformation || {};
                            const diKey = Object.keys(di)[0] || "";
                            const diVal = diKey ? di[diKey] : "";

                            const codes = Array.isArray(it?.serviceTypeCodes) ? it.serviceTypeCodes : [];
                            const names = Array.isArray(it?.serviceTypes) ? it.serviceTypes : [];
                            const services = codes.map((c, i) => `${c} ${names[i] ?? names[0] ?? ""}`.trim()).filter(Boolean);

                            const pushRow = (e, keySuf) => {
                              const name = (e?.entityName || "").trim();
                              const mid = (e?.entityMiddlename || "").trim();
                              const first = (e?.entityFirstname || "").trim();

                              const sig = [
                                name.toLowerCase(),
                                mid.toLowerCase(),
                                first.toLowerCase(),
                                diKey.toLowerCase(),
                                String(diVal).toLowerCase(),
                                services.map(s => s.toLowerCase()).join("|")
                              ].join("||");

                              if (seen.has(sig)) return;
                              seen.add(sig);

                              rows.push(
                                <div key={`${idx}-${keySuf}`} className="docpro_grid docpro_grid_gap_10" style={{ ...grid("1fr 0.7fr 0.9fr 1.6fr 1.8fr"), padding: "10px 0", borderTop: rows.length ? "1px solid #f3f4f6" : "none" }}>
                                  <div className="docpro_section_value">{name || "-"}</div>
                                  <div className="docpro_section_value">{mid || "-"}</div>
                                  <div className="docpro_section_value">{first || "-"}</div>

                                  <div className="docpro_section_value">
                                    {diKey ? (
                                      <>
                                        <span style={{ color: "#6b7280", marginRight: 6 }}>{diKey}</span>
                                        <span>{formatDateAsMDY(diVal)}</span>
                                      </>
                                    ) : "-"}
                                  </div>

                                  <div className="docpro_section_value">
                                    {services.length ? (
                                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        {services.map((s, i) => (
                                          <span key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: "2px 8px", fontSize: 12 }}>
                                            {/^([A-Z0-9]+)/i.test(s) ? (
                                              <>
                                                <span style={{ color: "#2563eb", fontWeight: 600, marginRight: 6 }}>
                                                  {s.match(/^([A-Z0-9]+)/i)[1]}
                                                </span>
                                                <span>{s.replace(/^([A-Z0-9]+)\s*/i, "")}</span>
                                              </>
                                            ) : s}
                                          </span>
                                        ))}
                                      </div>
                                    ) : "-"}
                                  </div>
                                </div>
                              );
                            };

                            if (it?.benefitsRelatedEntity) pushRow(it.benefitsRelatedEntity, "main");
                            (it?.benefitsRelatedEntities || []).forEach((e, j) => pushRow(e, `rel-${j}`));
                          });

                          return rows.length ? rows : (
                            <div style={{ padding: "10px 0", color: "#6b7280" }}>Primary care provider not found.</div>
                          );
                        })()}
                      </div>

                      <h3 className="docpro_section_title docpro_section_divider">
                        Financials&nbsp;&nbsp;
                        <span className="docpro_empty_badge">
                          <span className="docpro_fw_400" style={{ color: "#2563eb" }}>98</span>
                          <span className="docpro_fw_400" style={{ color: "#111827" }}>Professional (Physician) Visit - Office</span>
                        </span>
                      </h3>
                      <div className="docpro_section_body">
                        {/* header row */}
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("0.9fr 0.7fr 0.8fr 1.8fr 1fr"), paddingBottom: 5 }}>
                          <div>Entity Type</div>
                          <div>Amount</div>
                          <div>Coverage level</div>
                          <div>Message</div>
                          <div>Time period</div>
                        </div>

                        {/* rows */}
                        {(() => {
                          const money = (v) => {
                            if (!v) return "-";
                            const s = String(v).replace(/[^\d.-]/g, "");
                            if (s === "") return "-";
                            return `$${s}`;
                          };
                          const percent = (v) => {
                            if (!v) return "-";
                            const n = Number(v);
                            if (Number.isFinite(n)) return `${Math.round(n * 100)}%`;
                            const m = String(v).match(/(\d+(?:\.\d+)?)%/);
                            return m ? m[0] : "-";
                          };
                          const pickAmount = (b) => {
                            if (b?.benefitAmount) return money(b.benefitAmount);
                            if (b?.benefitPercent) return percent(b.benefitPercent);
                            const txt = b?.additionalInformation?.map(x => x?.description).join(" ") || "";
                            const pm = txt.match(/(\d+(?:\.\d+)?)%/);
                            if (pm) return pm[0];
                            const dm = txt.match(/\$?\s*\d+(?:\.\d{1,2})?/);
                            if (dm) return dm[0].replace(/\s+/g, "");
                            return "-";
                          };

                          const wanted = (eligibilityData?.benefitsInformation || []).filter(b => {
                            const has98 = Array.isArray(b?.serviceTypeCodes) && b.serviceTypeCodes.includes("98");
                            const name = String(b?.name || "").toLowerCase();
                            return (
                              has98 &&
                              (
                                name.includes("deductible") ||
                                name.includes("copay") ||
                                name.includes("co-pay") ||
                                name.includes("co-payment") ||
                                name.includes("coinsurance") ||
                                (b?.additionalInformation || []).some(
                                  x => /copay|co-pay|co-payment|coinsurance|deductible/i.test(x?.description || "")
                                )
                              )
                            );
                          });

                          return wanted.length ? (
                            wanted.map((b, i) => {
                              const message = b?.additionalInformation?.map(x => x?.description).filter(Boolean).join(" • ") || "-";
                              return (
                                <div
                                  key={i}
                                  className="docpro_grid docpro_grid_gap_10"
                                  style={{
                                    ...grid("0.9fr 0.7fr 0.8fr 1.8fr 1fr"),
                                    padding: "10px 0",
                                    borderTop: "1px solid #f3f4f6",
                                  }}
                                >
                                  <div className="docpro_section_value">{b?.name || "-"}</div>
                                  <div className="docpro_section_value">{pickAmount(b)}</div>
                                  <div className="docpro_section_value">{b?.coverageLevel || "-"}</div>
                                  <div className="docpro_section_value">{message}</div>
                                  <div className="docpro_section_value">{b?.timeQualifier || "-"}</div>
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ padding: "10px 0", color: "#6b7280" }}>
                              No deductible, copay, or coinsurance records for service code 98.
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    }
                  </>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}